import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Construction, Activity, Clock, History } from "lucide-react";
import { useAuth } from "@/admin/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK = [
  { to: "/admin/hero", label: "Hero", desc: "Titolo, badge, CTA" },
  { to: "/admin/lore", label: "Lore", desc: "Storia della città" },
  { to: "/admin/universe", label: "Universo (Saga)", desc: "4 Atti + Architettura del Potere" },
  { to: "/admin/factions", label: "Forze dell'Ordine", desc: "4 fazioni" },
  { to: "/admin/chapters", label: "Regolamento", desc: "12 capitoli + tabella sanzioni" },
  { to: "/admin/gallery", label: "Galleria", desc: "Foto + upload" },
  { to: "/admin/character", label: "Character", desc: "Showcase whitelist" },
  { to: "/admin/links", label: "Link & Social", desc: "Discord, Cfx, social" },
  { to: "/admin/media", label: "Media Library", desc: "Cassaforte file" },
  { to: "/admin/bible", label: "Bibbia (Staff)", desc: "Vault interno · /staff/bible" },
];

// Friendly meta per section name shown in audit log
const SECTION_META = {
  hero: { label: "Hero", to: "/admin/hero", color: "#D4AF37" },
  lore: { label: "Lore", to: "/admin/lore", color: "#A78BFA" },
  universe: { label: "Universo (Saga)", to: "/admin/universe", color: "#FFB100" },
  features: { label: "Features", to: "/admin/features", color: "#00D4FF" },
  factions: { label: "Forze dell'Ordine", to: "/admin/factions", color: "#FFB100" },
  character: { label: "Character", to: "/admin/character", color: "#E26B6B" },
  gallery: { label: "Galleria", to: "/admin/gallery", color: "#34D399" },
  rules: { label: "Regolamento", to: "/admin/chapters", color: "#D4AF37" },
  links: { label: "Link & Social", to: "/admin/links", color: "#00D4FF" },
  bible: { label: "Bibbia (Staff)", to: "/admin/bible", color: "#E26B6B" },
};

const sectionMeta = (s) =>
  SECTION_META[s] || { label: s || "—", to: "/admin", color: "#B6B6BE" };

const fmtAgo = (iso) => {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "ora";
    if (s < 3600) return `${Math.floor(s / 60)} min fa`;
    if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "—";
  }
};

export default function AdminOverview() {
  const { user } = useAuth();
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/audit`, { withCredentials: true });
        setAudit(data);
      } catch {
        /* silent */
      }
    })();
  }, []);

  return (
    <div data-testid="admin-overview">
      <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
        Benvenuto
      </p>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-wide mt-2 leading-none">
        Ciao, <span className="text-gold glow-gold">{user?.name || "Admin"}</span>
      </h1>
      <p className="mt-5 text-[#B6B6BE] text-base sm:text-lg leading-relaxed max-w-2xl">
        Da qui gestisci ogni sezione di pureliferp.it. Tutte le modifiche sono live
        sul sito non appena salvi. Usa <kbd className="px-2 py-0.5 rounded bg-[#0B0B0F] border border-[#D4AF37]/30 text-[#D4AF37] text-xs">⌘S</kbd> in qualsiasi editor per salvare al volo.
      </p>

      <div className="mt-9 grid lg:grid-cols-3 gap-4">
        <div
          className="lg:col-span-2 rounded-2xl p-6"
          style={{
            background:
              "linear-gradient(180deg, rgba(22,22,29,0.6), rgba(11,11,15,0.85))",
            border: "1px solid rgba(212,175,55,0.18)",
          }}
        >
          <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4 flex items-center gap-2">
            <Activity size={12} /> Attività recente
          </p>
          {audit.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <History size={28} className="text-[#B6B6BE]/40 mb-3" strokeWidth={1.5} />
              <p className="text-sm text-[#B6B6BE]">Nessuna modifica registrata ancora.</p>
              <p className="text-xs text-[#B6B6BE]/55 mt-1">
                Le ultime 5 modifiche compariranno qui dopo il primo salvataggio.
              </p>
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="admin-activity-feed">
              {audit.slice(0, 5).map((a, i) => {
                const meta = sectionMeta(a.section);
                return (
                  <li key={i} data-testid={`audit-item-${i}`}>
                    <Link
                      to={meta.to}
                      className="group flex items-center gap-3 text-sm py-2.5 px-3 rounded-xl hover:bg-white/[0.03] transition-colors"
                    >
                      <span
                        className="font-mono-pl text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-md border shrink-0 min-w-[110px] text-center"
                        style={{
                          color: meta.color,
                          borderColor: `${meta.color}55`,
                          background: `${meta.color}10`,
                        }}
                      >
                        {meta.label}
                      </span>
                      <span className="text-[#F5F5F5] truncate flex-1 group-hover:text-[#D4AF37] transition-colors">
                        {a.by_email}
                      </span>
                      <span className="text-xs text-[#B6B6BE] flex items-center gap-1 shrink-0 group-hover:text-[#F5F5F5] transition-colors">
                        <Clock size={10} /> {fmtAgo(a.at)}
                      </span>
                      <ArrowUpRight
                        size={13}
                        strokeWidth={2.2}
                        className="text-[#B6B6BE]/40 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-[#D4AF37] transition-all shrink-0"
                      />
                    </Link>
                  </li>
                );
              })}
              {audit.length > 5 && (
                <li className="pt-2 mt-1 border-t border-white/[0.04]">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE]/60 px-3">
                    Sono mostrate le 5 modifiche più recenti su {audit.length}.
                  </p>
                </li>
              )}
            </ul>
          )}
        </div>

        <div
          className="rounded-2xl p-6"
          style={{
            background:
              "linear-gradient(180deg, rgba(22,22,29,0.6), rgba(11,11,15,0.85))",
            border: "1px solid rgba(0,212,255,0.18)",
          }}
        >
          <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#00D4FF] mb-4">
            Shortcut
          </p>
          <ul className="space-y-3 text-sm text-[#B6B6BE]">
            <li><kbd className="px-2 py-0.5 rounded bg-[#0B0B0F] border border-white/15 text-[#F5F5F5] text-xs">⌘S</kbd> Salva editor</li>
            <li><kbd className="px-2 py-0.5 rounded bg-[#0B0B0F] border border-white/15 text-[#F5F5F5] text-xs">Esc</kbd> Chiudi drawer</li>
            <li className="text-xs pt-3 border-t border-white/[0.06]">
              <Construction size={12} className="inline mr-1 text-[#D4AF37]" />
              Tutti gli editor includono ora la <strong className="text-[#F5F5F5]">Anteprima live</strong> in split-screen.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
          Sezioni
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {QUICK.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`admin-quick-${q.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="group rounded-2xl p-6 transition-all"
              style={{
                background:
                  "linear-gradient(180deg, rgba(22,22,29,0.65), rgba(11,11,15,0.85))",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-xl uppercase tracking-wide text-[#F5F5F5] group-hover:text-[#D4AF37] transition-colors">
                    {q.label}
                  </p>
                  <p className="text-sm text-[#B6B6BE] mt-1.5">{q.desc}</p>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-[#B6B6BE] group-hover:text-[#D4AF37] -translate-x-1 group-hover:translate-x-0 transition-all"
                  strokeWidth={2.4}
                />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
